/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ArrowLeft, CalendarCheck, IndianRupee, Stethoscope, Users } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '../../components/ui/badge.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { StatCard } from '../../components/ui/stat-card.js';
import { type ClinicDetail } from '../../lib/api/sa.js';
import { useSaClinic } from '../../lib/queries/sa.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';

const DASH = '—';
const CARD_CLASS =
  'rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900';
const ROW_LABEL = 'text-sm text-slate-500 dark:text-slate-400';
const ROW_VALUE = 'text-sm font-medium text-slate-800 dark:text-white';
const DATE_FMT = 'dd MMM yyyy';

function InfoRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className={ROW_LABEL}>{label}</span>
      <span className={ROW_VALUE}>{value}</span>
    </div>
  );
}

function Overview({ clinic }: { readonly clinic: ClinicDetail }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={CARD_CLASS}>
      <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t('sa.detail.overview')}
      </h2>
      <InfoRow label={t('sa.detail.legalName')} value={clinic.legalName ?? DASH} />
      <InfoRow label={t('sa.detail.contactEmail')} value={clinic.contactEmail} />
      <InfoRow label={t('sa.detail.contactPhone')} value={clinic.contactPhone} />
      <InfoRow label={t('sa.detail.timezone')} value={clinic.timezone} />
      <InfoRow label={t('sa.detail.createdAt')} value={formatIst(clinic.createdAt, DATE_FMT)} />
    </div>
  );
}

function Subscription({ clinic }: { readonly clinic: ClinicDetail }): JSX.Element {
  const { t } = useTranslation();
  const sub = clinic.subscription;
  return (
    <div className={CARD_CLASS}>
      <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t('sa.detail.subscription')}
      </h2>
      {sub === null ? (
        <p className={ROW_LABEL}>{t('sa.detail.noSubscription')}</p>
      ) : (
        <>
          <InfoRow label={t('sa.detail.plan')} value={sub.planName ?? DASH} />
          <InfoRow label={t('sa.detail.subStatus')} value={sub.status ?? DASH} />
          <InfoRow label={t('sa.detail.mrr')} value={formatInr(sub.mrrPaise)} />
          <InfoRow
            label={t('sa.detail.periodEnd')}
            value={sub.currentPeriodEnd !== null ? formatIst(sub.currentPeriodEnd, DATE_FMT) : DASH}
          />
        </>
      )}
    </div>
  );
}

function CountCards({ clinic }: { readonly clinic: ClinicDetail }): JSX.Element {
  const { t } = useTranslation();
  const c = clinic.counts;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        icon={Stethoscope}
        label={t('sa.detail.therapists')}
        value={String(c.therapists)}
        accent="bg-[var(--accent)]"
      />
      <StatCard
        icon={Users}
        label={t('sa.detail.activeClients')}
        value={String(c.activeClients)}
        accent="bg-emerald-600"
      />
      <StatCard
        icon={CalendarCheck}
        label={t('sa.detail.sessions')}
        value={String(c.sessions)}
        accent="bg-sky-600"
      />
      <StatCard
        icon={IndianRupee}
        label={t('sa.detail.invoiced')}
        value={formatInr(c.invoicedPaise)}
        accent="bg-orange-500"
      />
    </div>
  );
}

function RecentAudit({ clinic }: { readonly clinic: ClinicDetail }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={CARD_CLASS}>
      <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t('sa.detail.recentAudit')}
      </h2>
      {clinic.recentAudit.length === 0 ? (
        <p className={ROW_LABEL}>{t('sa.detail.noAudit')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {clinic.recentAudit.map((entry) => (
            <li
              key={`${entry.at}-${entry.action}`}
              className="flex items-center justify-between gap-4 py-2"
            >
              <div>
                <p className={ROW_VALUE}>{entry.action}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{entry.actorType}</p>
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {formatIst(entry.at, 'dd MMM yyyy, HH:mm')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Super Admin → Clinic detail (`/sa/clinics/:id`). */
export function SaClinicDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useSaClinic(id);
  const clinic = query.data;

  const goBack = useCallback(() => {
    void navigate('/sa/clinics');
  }, [navigate]);

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={goBack}
        className="focus-ring mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('sa.detail.back')}
      </button>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={clinic === undefined}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('sa.detail.notFound')}
      >
        {clinic !== undefined && (
          <>
            <PageHeader
              title={clinic.name}
              action={<Badge label={t(`sa.clinicStatus.${clinic.status}`, clinic.status)} />}
            />
            <div className="mb-6">
              <CountCards clinic={clinic} />
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <Overview clinic={clinic} />
              <Subscription clinic={clinic} />
            </div>
            <div className="mt-6">
              <RecentAudit clinic={clinic} />
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
